import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Input,
  Label,
  Message,
  Pagination,
  Popup,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  API,
  loadChannelModels,
  setPromptShown,
  shouldShowPrompt,
  showError,
  showFormalPricingWarningsV87,
  showInfo,
  showSuccess,
  timestamp2string,
} from '../helpers';

import { CHANNEL_OPTIONS } from '../constants';
import { isMinerUChannelV913 } from '../pages/Channel/mineruConfigV913';
import { ChannelImageResponseBadgesV103 } from '../pages/Channel/ImageResponsePolicyV103';
import { renderGroup, renderNumber } from '../helpers/render';
import PageSizeSelectV8, { readPageSizeV8 } from './PageSizeSelectV8';
import {
  useSessionViewFieldV81,
  useSessionViewStateV81,
} from '../hooks/useSessionViewStateV81';

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

let type2label = undefined;

function renderType(type, t) {
  if (!type2label) {
    type2label = new Map();
    for (let i = 0; i < CHANNEL_OPTIONS.length; i++) {
      type2label[CHANNEL_OPTIONS[i].value] = CHANNEL_OPTIONS[i];
    }
    type2label[0] = {
      value: 0,
      text: t('channel.table.status_unknown'),
      color: 'grey',
    };
  }
  return (
    <Label basic color={type2label[type]?.color}>
      {type2label[type] ? type2label[type].text : type}
    </Label>
  );
}

function renderBalance(type, balance, t) {
  switch (type) {
    case 1:
      if (balance === 0) {
        return <span>{t('channel.table.balance_not_supported')}</span>;
      }
      return <span>${balance.toFixed(2)}</span>;
    case 4:
      return <span>楼{balance.toFixed(2)}</span>;
    case 8:
      return <span>${balance.toFixed(2)}</span>;
    case 5:
      return <span>楼{(balance / 10000).toFixed(2)}</span>;
    case 10:
      return <span>{renderNumber(balance)}</span>;
    case 12:
      return <span>楼{balance.toFixed(2)}</span>;
    case 13:
      return <span>{renderNumber(balance)}</span>;
    case 20:
      return <span>${balance.toFixed(2)}</span>;
    case 36:
      return <span>楼{balance.toFixed(2)}</span>;
    case 44:
      return <span>楼{balance.toFixed(2)}</span>;
    default:
      return <span>{t('channel.table.balance_not_supported')}</span>;
  }
}

function isShowDetail() {
  return localStorage.getItem('show_detail') === 'true';
}

const promptID = 'detail';

const ChannelsTable = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewState, setViewState] = useSessionViewStateV81('channels', {
    searchKeyword: '',
    searchGroup: '',
    showDetail: isShowDetail(),
    activePage: 1,
    pageSize: readPageSizeV8('v8-page-size-channels', 10),
  });
  const [searchKeyword, setSearchKeyword] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'searchKeyword'
  );
  const [searchGroup, setSearchGroup] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'searchGroup'
  );
  const [configuredGroups, setConfiguredGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [updatingBalance, setUpdatingBalance] = useState(false);
  const [reorderingChannelAction, setReorderingChannelAction] = useState('');
  const [channelOrderDrafts, setChannelOrderDrafts] = useState({});
  const [modelAudit, setModelAudit] = useState(null);
  const [modelAuditLoading, setModelAuditLoading] = useState(false);
  const [showPrompt, setShowPrompt] = useState(shouldShowPrompt(promptID));
  const [showDetail, setShowDetail] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'showDetail'
  );
  const [activePage, setActivePage] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'activePage'
  );
  const [pageSize, setPageSize] = useSessionViewFieldV81(
    viewState,
    setViewState,
    'pageSize'
  );

  const processChannelData = (channel) => {
    if (channel.models === '') {
      channel.models = [];
      channel.test_model = '';
    } else {
      channel.models = channel.models.split(',');
      if (channel.models.length > 0) {
        channel.test_model = channel.models[0];
      }
      channel.model_options = channel.models.map((model) => {
        return {
          key: model,
          text: model,
          value: model,
        };
      });
    }
    return channel;
  };

  const loadChannels = async (keyword = '') => {
    const endpoint = keyword
      ? `/api/channel/search?keyword=${encodeURIComponent(keyword)}`
      : '/api/channel/?scope=all';
    const res = await API.get(endpoint);
    const { success, message, data } = res.data;
    if (success) {
      setChannels(data.map(processChannelData));
      setChannelOrderDrafts({});
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const res = await API.get('/api/group/');
      const { success, message, data } = res.data;
      if (success) {
        setConfiguredGroups(Array.isArray(data) ? data : []);
      } else {
        showError(message || t('channel.filter_group_load_failed'));
      }
    } catch (error) {
      showError(error.message || t('channel.filter_group_load_failed'));
    } finally {
      setGroupsLoading(false);
    }
  }, [t]);

  const refresh = async () => {
    setLoading(true);
    await loadChannels(searchKeyword);
  };

  const toggleShowDetail = () => {
    setShowDetail(!showDetail);
    localStorage.setItem('show_detail', (!showDetail).toString());
  };

  useEffect(() => {
    loadChannels(searchKeyword)
      .then()
      .catch((reason) => {
        showError(reason);
      });
    loadChannelModels().then();
    loadGroups().then();
    API.get('/api/channel/model-audit').then((res) => {
      if (res.data.success) {
        setModelAudit(res.data.data);
      }
    });
    // This is the route-entry load; later filter changes are submitted explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGroups]);

  useEffect(() => {
    if (modelAudit?.status !== 'running') {
      return undefined;
    }
    const timer = setInterval(async () => {
      const res = await API.get('/api/channel/model-audit');
      if (res.data.success) {
        setModelAudit(res.data.data);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [modelAudit?.status]);

  const manageChannel = async (id, action, idx, value) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/channel/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/channel/', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/channel/', data);
        break;
      case 'priority':
        if (value === '') {
          return;
        }
        data.priority = parseInt(value);
        res = await API.put('/api/channel/', data);
        break;
      case 'weight':
        if (value === '') {
          return;
        }
        data.weight = parseInt(value);
        if (data.weight < 0) {
          data.weight = 0;
        }
        res = await API.put('/api/channel/', data);
        break;
      default:
        return;
    }
    const { success, message, pricing_warnings } = res.data;
    if (success) {
      showFormalPricingWarningsV87(pricing_warnings, t);
      showSuccess(t('channel.messages.operation_success'));
      let channel = res.data.data;
      let newChannels = [...channels];
      if (action === 'delete') {
        newChannels[idx].deleted = true;
      } else if (channel) {
        newChannels[idx] = processChannelData({
          ...newChannels[idx],
          ...channel,
        });
      }
      setChannels(newChannels);
    } else {
      showError(message);
    }
  };

  const reorderChannelV2 = async (id, direction) => {
    const actionKey = `${id}-${direction}`;
    setReorderingChannelAction(actionKey);
    const res = await API.post('/api/channel/reorder', { id, direction });
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('channel.messages.operation_success'));
      await refresh();
    } else {
      showError(message);
    }
    setReorderingChannelAction('');
  };

  const setChannelDisplayOrderV3 = async (channel) => {
    const rawValue = channelOrderDrafts[channel.id] ?? channel.display_order;
    const displayOrder = Number(rawValue);
    if (!Number.isInteger(displayOrder) || displayOrder < 1) {
      showError(t('channel.messages.invalid_management_order'));
      return;
    }

    const actionKey = `${channel.id}-set`;
    setReorderingChannelAction(actionKey);
    try {
      const res = await API.post('/api/channel/reorder/set', {
        id: channel.id,
        display_order: displayOrder,
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('channel.messages.operation_success'));
        await refresh();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setReorderingChannelAction('');
    }
  };

  const renderStatus = (channel, t) => {
    let status = channel.status;
    let failures = channel.failures || 0;
    switch (status) {
      case 1:
        if (failures >= 4) {
          return (
            <Popup
              trigger={
                <Label basic color='red'>
                  失效 ({failures})
                </Label>
              }
              content='最近请求失败过多'
              basic
            />
          );
        } else if (failures > 0) {
          return (
            <Popup
              trigger={
                <Label basic color='orange'>
                  不稳定 ({failures})
                </Label>
              }
              content='最近有请求失败'
              basic
            />
          );
        }
        return (
          <Label basic color='green'>
            {t('channel.table.status_enabled')}
          </Label>
        );
      case 2:
        return (
          <Popup
            trigger={
              <Label basic color='red'>
                {t('channel.table.status_disabled')}
              </Label>
            }
            content={t('channel.table.status_disabled_tip')}
            basic
          />
        );
      case 3:
        return (
          <Popup
            trigger={
              <Label basic color='red'>
                {t('channel.table.status_auto_disabled')}
              </Label>
            }
            content={t('channel.table.status_auto_disabled_tip')}
            basic
          />
        );
      default:
        return (
          <Label basic color='grey'>
            {t('channel.table.status_unknown')}
          </Label>
        );
    }
  };

  const renderResponseTime = (responseTime, t) => {
    let time = responseTime / 1000;
    time = time.toFixed(2) + 's';
    if (responseTime === 0) {
      return (
        <Label basic color='grey'>
          {t('channel.table.not_tested')}
        </Label>
      );
    } else if (responseTime <= 1000) {
      return (
        <Label basic color='green'>
          {time}
        </Label>
      );
    } else if (responseTime <= 3000) {
      return (
        <Label basic color='olive'>
          {time}
        </Label>
      );
    } else if (responseTime <= 5000) {
      return (
        <Label basic color='yellow'>
          {time}
        </Label>
      );
    } else {
      return (
        <Label basic color='red'>
          {time}
        </Label>
      );
    }
  };

  const searchChannels = async () => {
    setSearching(true);
    setLoading(true);
    setActivePage(1);
    await loadChannels(searchKeyword);
    setSearching(false);
  };

  const switchTestModel = async (idx, model) => {
    let newChannels = [...channels];
    newChannels[idx].test_model = model;
    setChannels(newChannels);
  };

  const testChannel = async (id, name, idx, m) => {
    if (isMinerUChannelV913(channels[idx]?.type)) return;
    const res = await API.get(`/api/channel/test/${id}?model=${m}`);
    const { success, message, time, model } = res.data;
    if (success) {
      let newChannels = [...channels];
      newChannels[idx].response_time = time * 1000;
      newChannels[idx].test_time = Date.now() / 1000;
      setChannels(newChannels);
      showSuccess(
        t('channel.messages.test_success', { name, model, time, message })
      );
    } else {
      showError(message);
    }
    let newChannels = [...channels];
    newChannels[idx].response_time = time * 1000;
    newChannels[idx].test_time = Date.now() / 1000;
    setChannels(newChannels);
  };

  const testChannels = async (scope) => {
    const res = await API.get(`/api/channel/test?scope=${scope}`);
    const { success, message } = res.data;
    if (success) {
      showInfo(t('channel.messages.test_all_started'));
    } else {
      showError(message);
    }
  };

  const startModelAudit = async () => {
    setModelAuditLoading(true);
    try {
      const res = await API.post('/api/channel/model-audit?scope=all');
      const { success, message, data } = res.data;
      if (success) {
        setModelAudit(data);
        showInfo(
          'Model audit started: each configured model will run standard and JSON Object streaming Chat tests.'
        );
      } else {
        showError(message);
      }
    } finally {
      setModelAuditLoading(false);
    }
  };

  const downloadModelAudit = async (format) => {
    if (format === 'csv') {
      const res = await API.get('/api/channel/model-audit.csv', {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${modelAudit?.id || 'model-audit'}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }
    const blob = new Blob([JSON.stringify(modelAudit, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${modelAudit?.id || 'model-audit'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const deleteAllDisabledChannels = async () => {
    const res = await API.delete(`/api/channel/disabled`);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(
        t('channel.messages.delete_disabled_success', { count: data })
      );
      await refresh();
    } else {
      showError(message);
    }
  };

  const updateChannelBalance = async (id, name, idx) => {
    const res = await API.get(`/api/channel/update_balance/${id}/`);
    const { success, message, balance } = res.data;
    if (success) {
      let newChannels = [...channels];
      newChannels[idx].balance = balance;
      newChannels[idx].balance_updated_time = Date.now() / 1000;
      setChannels(newChannels);
      showSuccess(t('channel.messages.balance_update_success', { name }));
    } else {
      showError(message);
    }
  };

  const updateAllChannelsBalance = async () => {
    setUpdatingBalance(true);
    const res = await API.get(`/api/channel/update_balance`);
    const { success, message } = res.data;
    if (success) {
      showInfo(t('channel.messages.all_balance_updated'));
    } else {
      showError(message);
    }
    setUpdatingBalance(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortChannel = (key) => {
    if (channels.length === 0) return;
    setLoading(true);
    let sortedChannels = [...channels];
    sortedChannels.sort((a, b) => {
      if (!isNaN(a[key])) {
        return a[key] - b[key];
      } else {
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedChannels[0].id === channels[0].id) {
      sortedChannels.reverse();
    }
    setChannels(sortedChannels);
    setLoading(false);
  };

  const groupOptions = useMemo(() => {
    const groups = new Set(configuredGroups);
    channels.forEach((channel) => {
      (channel.group || '').split(',').forEach((group) => {
        const normalizedGroup = group.trim();
        if (normalizedGroup) {
          groups.add(normalizedGroup);
        }
      });
    });
    return [
      {
        key: 'all-groups',
        text: t('channel.filter_group_all'),
        value: '',
      },
      ...Array.from(groups)
        .sort((left, right) => left.localeCompare(right))
        .map((group) => ({
          key: group,
          text: group,
          value: group,
        })),
    ];
  }, [channels, configuredGroups, t]);

  const visibleChannels = channels.filter((channel) => {
    if (channel.deleted) {
      return false;
    }
    if (!searchGroup) {
      return true;
    }
    return (channel.group || '')
      .split(',')
      .map((group) => group.trim())
      .includes(searchGroup);
  });
  const totalPages = Math.max(1, Math.ceil(visibleChannels.length / pageSize));
  const pagedChannels = visibleChannels.slice(
    (activePage - 1) * pageSize,
    activePage * pageSize
  );

  useEffect(() => {
    if (loading) return;
    setActivePage((currentPage) => Math.min(currentPage, totalPages));
    // setActivePage is a stable field setter from the session-view hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, totalPages]);

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setActivePage(1);
  };

  return (
    <>
      <Form onSubmit={searchChannels}>
        <Form.Group>
          <Form.Input
            width={12}
            icon='search'
            fluid
            iconPosition='left'
            placeholder={t('channel.search')}
            value={searchKeyword}
            loading={searching}
            onChange={handleKeywordChange}
            action={
              <Button
                type='submit'
                icon='search'
                loading={searching}
                disabled={searching}
              />
            }
          />
          <Form.Dropdown
            width={4}
            fluid
            selection
            search
            loading={groupsLoading}
            placeholder={t('channel.filter_group')}
            noResultsMessage={t('channel.filter_group_no_results')}
            options={groupOptions}
            value={searchGroup}
            onChange={(event, { value }) => {
              setSearchGroup(value);
              setActivePage(1);
            }}
          />
        </Form.Group>
      </Form>
      {showPrompt && (
        <Message
          onDismiss={() => {
            setShowPrompt(false);
            setPromptShown(promptID);
          }}
        >
          {t('channel.balance_notice')}
          <br />
          {t('channel.test_notice')}
          <br />
          {t('channel.detail_notice')}
        </Message>
      )}
      <div
        className='management-table-scroll-v72 management-channel-scroll-v81'
        role='region'
        aria-label='Scrollable data table'
        tabIndex={0}
      >
        <Table
          basic={'very'}
          compact
          size='small'
          unstackable
          className='management-table-v72 management-channel-table-v72'
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('id');
                }}
              >
                {t('channel.table.id')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('name');
                }}
              >
                {t('channel.table.name')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('group');
                }}
              >
                {t('channel.table.group')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('type');
                }}
              >
                {t('channel.table.type')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('status');
                }}
              >
                {t('channel.table.status')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('response_time');
                }}
              >
                {t('channel.table.response_time')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('balance');
                }}
              >
                {t('channel.table.balance')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortChannel('priority');
                }}
                hidden={!showDetail}
              >
                {t('channel.table.priority')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('channel.table.management_order')}
              </Table.HeaderCell>
              <Table.HeaderCell hidden={!showDetail}>
                {t('channel.table.test_model')}
              </Table.HeaderCell>
              <Table.HeaderCell>{t('channel.table.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {pagedChannels.map((channel) => {
              const idx = channels.findIndex((item) => item.id === channel.id);
              return (
                <Table.Row key={channel.id}>
                  <Table.Cell>{channel.id}</Table.Cell>
                  <Table.Cell>
                    {channel.name ? channel.name : t('channel.table.no_name')}
                    <ChannelImageResponseBadgesV103 channel={channel} t={t} />
                    {channel.has_disabled_keys && (
                      <Label
                        color='red'
                        size='tiny'
                        style={{ marginLeft: '4px' }}
                      >
                        有失效的密钥
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{renderGroup(channel.group)}</Table.Cell>
                  <Table.Cell>{renderType(channel.type, t)}</Table.Cell>
                  <Table.Cell>{renderStatus(channel, t)}</Table.Cell>
                  <Table.Cell>
                    <Popup
                      content={
                        channel.test_time
                          ? renderTimestamp(channel.test_time)
                          : t('channel.table.not_tested')
                      }
                      key={channel.id}
                      trigger={renderResponseTime(channel.response_time, t)}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Popup
                      trigger={
                        <span
                          onClick={() => {
                            updateChannelBalance(channel.id, channel.name, idx);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {renderBalance(channel.type, channel.balance, t)}
                        </span>
                      }
                      content={t('channel.table.click_to_update')}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell hidden={!showDetail}>
                    <Popup
                      trigger={
                        <Input
                          type='number'
                          defaultValue={channel.priority}
                          onBlur={(event) => {
                            manageChannel(
                              channel.id,
                              'priority',
                              idx,
                              event.target.value
                            );
                          }}
                        >
                          <input style={{ maxWidth: '60px' }} />
                        </Input>
                      }
                      content={t('channel.table.priority_tip')}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Popup
                      trigger={
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '4px',
                          }}
                        >
                          <Input
                            size='mini'
                            type='number'
                            min={1}
                            step={1}
                            value={
                              channelOrderDrafts[channel.id] ??
                              channel.display_order ??
                              ''
                            }
                            onChange={(event, { value }) => {
                              setChannelOrderDrafts((current) => ({
                                ...current,
                                [channel.id]: value,
                              }));
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                setChannelDisplayOrderV3(channel);
                              }
                            }}
                            style={{ width: '72px' }}
                          />
                          <Button
                            compact
                            icon='save'
                            size='tiny'
                            type='button'
                            title={t('channel.buttons.save_order')}
                            aria-label={t('channel.buttons.save_order')}
                            loading={
                              reorderingChannelAction === `${channel.id}-set`
                            }
                            disabled={reorderingChannelAction !== ''}
                            onClick={() => {
                              setChannelDisplayOrderV3(channel);
                            }}
                          />
                          <Button
                            size='tiny'
                            type='button'
                            loading={
                              reorderingChannelAction === `${channel.id}-up`
                            }
                            onClick={() => {
                              reorderChannelV2(channel.id, 'up');
                            }}
                          >
                            {t('channel.buttons.move_up')}
                          </Button>
                          <Button
                            size='tiny'
                            type='button'
                            loading={
                              reorderingChannelAction === `${channel.id}-down`
                            }
                            onClick={() => {
                              reorderChannelV2(channel.id, 'down');
                            }}
                          >
                            {t('channel.buttons.move_down')}
                          </Button>
                        </div>
                      }
                      content={t('channel.table.management_order_tip')}
                      basic
                    />
                  </Table.Cell>
                  <Table.Cell hidden={!showDetail}>
                    <Dropdown
                      disabled={isMinerUChannelV913(channel.type)}
                      placeholder={t('channel.table.select_test_model')}
                      selection
                      options={channel.model_options}
                      defaultValue={channel.test_model}
                      onChange={(event, data) => {
                        switchTestModel(idx, data.value);
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '2px',
                        rowGap: '6px',
                      }}
                    >
                      <Button
                        size={'tiny'}
                        positive
                        disabled={isMinerUChannelV913(channel.type)}
                        title={
                          isMinerUChannelV913(channel.type)
                            ? t('channel.edit.mineru_v913.dedicated_protocol')
                            : undefined
                        }
                        onClick={() => {
                          testChannel(
                            channel.id,
                            channel.name,
                            idx,
                            channel.test_model
                          );
                        }}
                      >
                        {t('channel.buttons.test')}
                      </Button>
                      <Popup
                        trigger={
                          <Button size='tiny' negative>
                            {t('channel.buttons.delete')}
                          </Button>
                        }
                        on='click'
                        flowing
                        hoverable
                      >
                        <Button
                          size={'tiny'}
                          negative
                          onClick={() => {
                            manageChannel(channel.id, 'delete', idx);
                          }}
                        >
                          {t('channel.buttons.confirm_delete')} {channel.name}
                        </Button>
                      </Popup>
                      <Button
                        size={'tiny'}
                        onClick={() => {
                          manageChannel(
                            channel.id,
                            channel.status === 1 ? 'disable' : 'enable',
                            idx
                          );
                        }}
                      >
                        {channel.status === 1
                          ? t('channel.buttons.disable')
                          : t('channel.buttons.enable')}
                      </Button>
                      <Button
                        size={'tiny'}
                        as={Link}
                        to={'/channel/edit/' + channel.id}
                      >
                        {t('channel.buttons.edit')}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>

          {modelAudit && (
            <Table.Footer fullWidth>
              <Table.Row>
                <Table.HeaderCell colSpan={showDetail ? '11' : '9'}>
                  <Message
                    info={modelAudit.status === 'running'}
                    positive={
                      modelAudit.status === 'completed' &&
                      modelAudit.failed === 0
                    }
                    negative={
                      modelAudit.status === 'failed' ||
                      (modelAudit.status === 'completed' &&
                        modelAudit.failed > 0)
                    }
                    content={`Model audit: ${modelAudit.status}; progress ${
                      modelAudit.completed || 0
                    }/${modelAudit.total || 0}; passed ${
                      modelAudit.succeeded || 0
                    }; failed ${modelAudit.failed || 0}${
                      modelAudit.current_channel
                        ? `; current ${modelAudit.current_channel} / ${modelAudit.current_model}`
                        : ''
                    }`}
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
      </div>
      <div className='management-table-footer-v81'>
        <div className='management-table-actions-v81'>
          <Button size='tiny' as={Link} to='/channel/add' loading={loading}>
            {t('channel.buttons.add')}
          </Button>
          <Button
            size='tiny'
            loading={loading}
            onClick={() => {
              testChannels('all');
            }}
          >
            {t('channel.buttons.test_all')}
          </Button>
          <Button
            size='tiny'
            loading={loading}
            onClick={() => {
              testChannels('disabled');
            }}
          >
            {t('channel.buttons.test_disabled')}
          </Button>
          <Button
            size='tiny'
            loading={modelAuditLoading || modelAudit?.status === 'running'}
            onClick={startModelAudit}
          >
            Audit All Models
          </Button>
          {modelAudit && (
            <>
              <Button
                size='tiny'
                disabled={modelAudit.status === 'running'}
                onClick={() => downloadModelAudit('json')}
              >
                Download Audit JSON
              </Button>
              <Button
                size='tiny'
                disabled={modelAudit.status === 'running'}
                onClick={() => downloadModelAudit('csv')}
              >
                Download Audit CSV
              </Button>
            </>
          )}
          <Popup
            trigger={
              <Button size='tiny' loading={loading}>
                {t('channel.buttons.delete_disabled')}
              </Button>
            }
            on='click'
            flowing
            hoverable
          >
            <Button
              size='tiny'
              loading={loading}
              negative
              onClick={deleteAllDisabledChannels}
            >
              {t('channel.buttons.confirm_delete_disabled')}
            </Button>
          </Popup>
          <Button
            size='tiny'
            onClick={updateAllChannelsBalance}
            loading={updatingBalance}
          >
            {t('channel.buttons.refresh_balance')}
          </Button>
          <Button size='tiny' onClick={refresh} loading={loading}>
            {t('channel.buttons.refresh')}
          </Button>
          <Button size='tiny' onClick={toggleShowDetail}>
            {showDetail
              ? t('channel.buttons.hide_detail')
              : t('channel.buttons.show_detail')}
          </Button>
        </div>
        <div className='list-pagination-v8'>
          <PageSizeSelectV8
            label={t('log.table.page_size')}
            storageKey='v8-page-size-channels'
            value={pageSize}
            onChange={handlePageSizeChange}
          />
          <Pagination
            activePage={activePage}
            onPageChange={(_, { activePage: nextPage }) =>
              setActivePage(Number(nextPage))
            }
            size='small'
            siblingRange={1}
            totalPages={totalPages}
          />
          <span>
            {t('channel.table.total', {
              count: visibleChannels.length,
            })}
          </span>
        </div>
      </div>
    </>
  );
};

export default ChannelsTable;
